import React from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  PillTabs,
  PillTabsList,
  PillTabsTriggerHint,
  PillTabsTrigger,
  PillTabsContent,
} from "~/react/ui/pill-tabs"
import { TooltipProvider } from "~/react/ui/tooltip"
import {
  Filters,
  clearFilters,
  departmentsList,
  filterFunction,
  sortFunction,
  type FiltersType,
} from "~/react/components/search/filters"
import {
  Result,
  ResultType,
} from "~/react/components/multi_domain_search/result"
import { type TabSelected } from "~/react/types/tabSelected"
import {
  leads_path,
  multi_domain_search_emails_search_index_path,
  multi_domain_search_search_index_path,
} from "~/lib/routes"
import { useAsyncQueuer } from "@tanstack/react-pacer"
import BulkActionsToolbar from "~/react/components/search/bulk-actions-toolbar"
import { Alert, AlertIcon, AlertContent } from "~/react/ui/alert"
import { DepartmentSection } from "~/react/components/search/department_section"
import { EmptyState } from "~/react/components/search/empty-state"
import { useCampaignsSave } from "~/react/hooks/useCampaignsSave"
import { useExportEmails } from "~/react/hooks/useExportEmails"
import { useJobTitleSuggestions } from "~/react/hooks/useJobTitleSuggestions"
import { useSplittedResults } from "../hooks/useSplittedResults"
import { useLocationMappings } from "../hooks/useLocationMappings"
import { LoadingSpinner } from "../ui/loading-spinner"
import { useEmailSearch } from "../hooks/useEmailSearch"
import { useLeadsListSave } from "../hooks/useLeadsListSave"
import { useRevealCost } from "../hooks/useRevealCost"
import { Snackbar, SnackbarText, SnackbarLink } from "../ui/snackbar"

const estimatedSize = 80

interface DomainSidepanelEmailsProps {
  emailIds: number[] | undefined
  numberOfResults: number
  genericEmailsCount: number
  domain: {
    value: string
    company_name?: string
    company_data: boolean
  }
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  numberOfCreditsLeft: number
  singleCreditsBucket: boolean
}

export default function DomainSidepanelEmails({
  emailIds: initialEmailIds,
  numberOfResults,
  genericEmailsCount,
  domain,
  numberOfCreditsLeft,
  singleCreditsBucket,
  leadsLists: initialLeadsLists,
  campaigns: initialCampaigns,
}: DomainSidepanelEmailsProps) {
  const [isLoading, setIsLoading] = React.useState(false)
  const [leadsLists, setLeadsLists] =
    React.useState<{ id: number; name: string }[]>(initialLeadsLists)
  const [campaigns, setCampaigns] =
    React.useState<{ id: number; name: string }[]>(initialCampaigns)
  const [tabSelected, setTabSelected] =
    React.useState<TabSelected>("all_people")
  const locationsMappings = useLocationMappings()
  const [filters, setFilters] = React.useState<FiltersType>({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: false,
    location: [],
  })
  const [showFilters, setShowFilters] = React.useState(false)
  const [showFindByName, setShowFindByName] = React.useState(false)
  const exportEmails = useExportEmails(domain.value)

  const {
    results,
    setResults,
    revealingIds,
    revealedIds,
    selectedIds,
    setSelectedIds,
    revealedLeadsListId,
    operationId,
    lastRevealedIds,
    handleReveal,
    handleRevealMultiple,
    handleSelect,
  } = useEmailSearch({
    source: "search",
    context: "search",
  })

  const revealingGeneric = React.useMemo(() => {
    return revealingIds.some(
      (id) => results.find((result) => result.id === id)?.type === "generic",
    )
  }, [revealingIds, results])

  // Loading hook for fetching emails
  const fetchEmailsFn = async (emailIds: number[]) => {
    const response = await fetch(
      multi_domain_search_emails_search_index_path(),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            ?.content,
        },
        body: JSON.stringify({ email_ids: emailIds }),
      },
    )
    const data = await response.json()

    setResults((prev) => {
      const existingIds = new Set(prev.map((result) => result.id))
      const newResults = data.emails.filter(
        (email) => !existingIds.has(email.id),
      )
      return [...prev, ...newResults]
    })
    if (queue.peekPendingItems().length === 0) {
      setIsLoading(false)
    }
  }
  const queue = useAsyncQueuer(fetchEmailsFn, { concurrency: 10, wait: 10 })

  // Load initial emails
  React.useEffect(() => {
    const fetchEmailIdsForDomain = async () => {
      const response = await fetch(multi_domain_search_search_index_path(), {
        method: "POST",
        body: JSON.stringify({ domains: domain.value }),
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            ?.content,
        },
      })
      const data = await response.json()
      const emailIds = data.email_ids
      const batchSize = 10000
      for (let i = 0; i < emailIds.length; i += batchSize) {
        queue.addItem(emailIds.slice(i, i + batchSize))
      }
    }
    if (initialEmailIds && initialEmailIds.length > 0) {
      setIsLoading(true)
      const batchSize = 5000

      for (let i = 0; i < initialEmailIds.length; i += batchSize) {
        queue.addItem(initialEmailIds.slice(i, i + batchSize))
      }
    } else if (initialEmailIds === undefined) {
      setIsLoading(true)
      // Fetch email IDs for the domain
      fetchEmailIdsForDomain()
    }
  }, [initialEmailIds])

  const handleLeadsListSave = useLeadsListSave(
    results,
    setLeadsLists,
    setResults,
  )
  // Filter and sort results
  const filteredResults = React.useMemo(() => {
    const baseFilteredResults = filterFunction(
      results,
      filters,
      locationsMappings,
      revealedIds,
    )

    return baseFilteredResults.sort(sortFunction)
  }, [results, filters, revealedIds])

  const { allPeopleResults, decisionMakersResults, genericResults } =
    useSplittedResults(filteredResults)

  React.useEffect(() => {
    if (
      tabSelected === "all_people" &&
      allPeopleResults.length === 0 &&
      genericResults.length > 0
    ) {
      setTabSelected("generic")
    }
  }, [tabSelected, allPeopleResults.length, genericResults.length])

  const jobTitles = useJobTitleSuggestions(
    results,
    filters,
    locationsMappings,
    revealedIds,
  )

  // Group results by department for the "All People" tab
  const resultsByDepartment = React.useMemo(() => {
    const personalEmails = filteredResults.filter(
      (result) => result.type === "personal",
    )

    // Group by department
    const grouped = personalEmails.reduce(
      (acc, result) => {
        const dept = result.department || null
        if (!acc[dept]) acc[dept] = []
        acc[dept].push(result)
        return acc
      },
      {} as Record<string, ResultType[]>,
    )

    // Convert to array and sort by department name
    return Object.entries(grouped).sort(([deptA], [deptB]) => {
      if (deptA === null) return 1 // Unknown department goes last
      if (deptB === null) return -1
      return deptA.localeCompare(deptB)
    })
  }, [filteredResults])

  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.hideAlreadySaved ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0

  // Bulk actions functions
  const saveLeads = async (listId: number | null) => {
    if (selectedIds.length > 0) {
      handleRevealMultiple(selectedIds, { leads_list_id: listId ?? undefined })
    }
  }
  const handleCampaignsSave = useCampaignsSave(results, campaigns, setCampaigns)
  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    saveLeads(listId)
  }

  // Calculate costs
  const { revealAllOperationCost } = useRevealCost({
    selectedIds,
    tabSelected,
    results,
    allPeopleResults,
    decisionMakersResults,
    genericResults,
    numberOfCreditsLeft,
  })

  const decisionMakersListRef = React.useRef(null)
  const genericListRef = React.useRef(null)

  const decisionMakersListVirtualizer = useVirtualizer({
    count: decisionMakersResults.length,
    getScrollElement: () => decisionMakersListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "decision_makers",
  })
  const genericListVirtualizer = useVirtualizer({
    count: genericResults.length,
    getScrollElement: () => genericListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "generic",
  })

  const genericRevealed = React.useMemo(() => {
    return revealingGeneric || results.some((result) => result.revealed)
  }, [results, revealingGeneric])

  const alreadyRevealedCount = selectedIds.filter(
    (id) => results.find((result) => result.id === id)?.revealed,
  ).length

  const snackbarLinkParams: Record<string, number | null> = {}
  if (operationId) {
    snackbarLinkParams.operation_id = operationId
  }
  if (revealedLeadsListId) {
    snackbarLinkParams.leads_list_id = revealedLeadsListId
  }
  if (lastRevealedIds.length === 1) {
    const result = results.find((result) => lastRevealedIds.includes(result.id))

    if (result) {
      snackbarLinkParams.email = result.email
    }
  }

  return (
    <>
      <TooltipProvider>
        <header className="ds-results__header">
          <h2
            className="ds-results__title"
            data-domain-search--search-target="resultsTitle"
          >
            <strong>
              {i18n.t("js.search.results", {
                count: numberOfResults || 0,
              })}
            </strong>{" "}
            {i18n.t("js.search.for")} {domain.value}
          </h2>
          <div className="ml-auto d-flex gap-2">
            <Button size="sm" onClick={() => setShowFilters(!showFilters)}>
              <span className="far fa-sliders-simple" aria-hidden="true"></span>
              Filters
              {showFilters ? (
                <span className="far fa-angle-up" aria-hidden="true"></span>
              ) : (
                <span className="far fa-angle-down" aria-hidden="true"></span>
              )}
            </Button>
            <Button
              size="sm"
              onClick={() => setShowFindByName(!showFindByName)}
            >
              <span className="far fa-circle-user" aria-hidden="true"></span>
              Find by Name
              {showFindByName ? (
                <span className="far fa-angle-up" aria-hidden="true"></span>
              ) : (
                <span className="far fa-angle-down" aria-hidden="true"></span>
              )}
            </Button>
          </div>
        </header>

        {showFilters && (
          <Filters
            id="company-emails-filters"
            filters={filters}
            setFilters={(newFilters) => setFilters(newFilters)}
            jobTitles={jobTitles}
            anyFiltersSet={anyFiltersSet}
            clearFilters={() => clearFilters(setFilters)}
            possibleFilters={[
              "department",
              "job_title",
              "already_saved",
              "verification_status",
              "required_field",
              "location",
            ]}
          />
        )}

        {showFindByName && (
          <form
            className="ds-finder"
            data-controller="domain-search--email-finder loading"
            data-domain-search--email-finder-domain-value={domain.value}
            data-loading-text-value=""
            data-domain-search--email-finder-version-value="domain_search"
            data-action="domain-search--email-finder#find loading#show domain-search--email-finder:loaded@window->loading#hide domain-search--email-finder:loaded@window->domain-search--search#updateResultsTitle"
          >
            <div className="ds-finder-form">
              <div className="ds-finder-form-name">
                <svg className="ds-finder-form-name__icon">
                  <use xlinkHref="#icon-email-finder" />
                </svg>
                <input
                  id="full-name-field"
                  className="ds-finder-form-name__field"
                  type="text"
                  placeholder={i18n.t(
                    "js.search.find_by_name.fullname_placeholder",
                  )}
                  data-domain-search--email-finder-target="name"
                  data-focus-element-target="element"
                />
              </div>
              <button
                className="ds-finder-form__submit"
                type="button"
                aria-label={i18n.t("js.search.find_by_name.find_this_person")}
                data-loading-target="element"
                data-action="domain-search--email-finder#find loading#show"
              >
                <span className="far fa-search"></span>
              </button>
            </div>
          </form>
        )}

        <turbo-frame id="domain_search_email_finder_email" />

        {/* Bulk Actions Toolbar - matches original ds-bulk-actions structure */}
        <BulkActionsToolbar
          className={selectedIds.length > 0 ? "is-visible" : ""}
          selectedIds={selectedIds}
          numberOfResults={numberOfResults}
          currentCredits={numberOfCreditsLeft}
          currentTab={tabSelected}
          alreadyRevealedCount={alreadyRevealedCount}
          numberOfResultsForCurrentTab={
            tabSelected === "all_people"
              ? allPeopleResults.length
              : tabSelected === "decision_makers"
                ? decisionMakersResults.length
                : genericResults.length
          }
          removeSelection={() => setSelectedIds([])}
          selectAll={() =>
            setSelectedIds(filteredResults.map((result) => result.id))
          }
          selectCurrentTab={() => {
            const resultsToSelect =
              tabSelected === "all_people"
                ? allPeopleResults
                : tabSelected === "decision_makers"
                  ? decisionMakersResults
                  : genericResults

            setSelectedIds((prev) => [
              ...new Set([
                ...prev,
                ...resultsToSelect.map((result) => result.id),
              ]),
            ])
          }}
          saveLeads={saveLeads}
          handleLeadManagerSave={handleLeadManagerSave}
          leadsLists={leadsLists}
          revealingIds={revealingIds}
          revealAllOperationCost={revealAllOperationCost}
          exportEmails={() => exportEmails(selectedIds)}
        />

        <div className="ds-results__body mb-4" id="domain_search_emails">
          {/* Tab Headers */}
          <div className="my-4">
            <PillTabs value={tabSelected}>
              <PillTabsList size="md" fullWidth className="my-4">
                <PillTabsTrigger
                  value="all_people"
                  onClick={() => setTabSelected("all_people")}
                >
                  {i18n.t("js.search.tabs.all_people")}
                  <PillTabsTriggerHint>
                    ·{" "}
                    {CurrencyHelper.numberWithDelimiter(
                      allPeopleResults.length,
                    )}
                  </PillTabsTriggerHint>
                </PillTabsTrigger>
                <PillTabsTrigger
                  value="decision_makers"
                  onClick={() => setTabSelected("decision_makers")}
                >
                  {i18n.t("js.search.tabs.decision_makers")}
                  <PillTabsTriggerHint>
                    ·{" "}
                    {CurrencyHelper.numberWithDelimiter(
                      decisionMakersResults.length,
                    )}
                  </PillTabsTriggerHint>
                </PillTabsTrigger>
                <PillTabsTrigger
                  value="generic"
                  onClick={() => setTabSelected("generic")}
                >
                  {i18n.t("js.search.tabs.generic")}
                  <PillTabsTriggerHint>
                    ·{" "}
                    {CurrencyHelper.numberWithDelimiter(genericResults.length)}
                  </PillTabsTriggerHint>
                </PillTabsTrigger>
              </PillTabsList>
              <PillTabsContent value="all_people">
                {Object.entries(departmentsList).map(([key, value]) => (
                  <DepartmentSection
                    key={key}
                    department={value}
                    title={value}
                    hideCompanyDetails={true}
                    results={
                      resultsByDepartment.find(
                        ([department, departmentResults]) => department === key,
                      )?.[1] || []
                    }
                    selectedIds={selectedIds}
                    onSelect={handleSelect}
                    handleReveal={handleReveal}
                    handleLeadsListSave={handleLeadsListSave}
                    handleCampaignsSave={handleCampaignsSave}
                    leadsLists={leadsLists}
                    campaigns={campaigns}
                    domain={domain}
                    defaultExpanded={
                      Object.entries(departmentsList)[0][0] === key
                    }
                  />
                ))}
                <DepartmentSection
                  key="unknown"
                  department="Department unknown"
                  title="Department unknown"
                  hideCompanyDetails={true}
                  results={
                    resultsByDepartment.find(
                      ([department, departmentResults]) =>
                        department === "null",
                    )?.[1] || []
                  }
                  selectedIds={selectedIds}
                  onSelect={handleSelect}
                  handleReveal={handleReveal}
                  handleLeadsListSave={handleLeadsListSave}
                  handleCampaignsSave={handleCampaignsSave}
                  leadsLists={leadsLists}
                  campaigns={campaigns}
                  domain={domain}
                  defaultExpanded={resultsByDepartment[0]?.[0] === "null"}
                />

                {isLoading && <LoadingSpinner />}
                {allPeopleResults.length === 0 &&
                  !isLoading &&
                  (anyFiltersSet ? (
                    <EmptyState clearFilters={clearFilters} />
                  ) : (
                    <EmptyState />
                  ))}
              </PillTabsContent>
              <PillTabsContent
                value="decision_makers"
                ref={decisionMakersListRef}
                style={{ height: "600px", overflow: "auto" }}
              >
                <div
                  style={
                    decisionMakersResults.length > 0
                      ? {
                          height: `${decisionMakersListVirtualizer.getTotalSize()}px`,
                          position: "relative",
                        }
                      : undefined
                  }
                >
                  {decisionMakersListVirtualizer
                    .getVirtualItems()
                    .map((virtualItem) => (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={decisionMakersListVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        <Result
                          result={decisionMakersResults[virtualItem.index]}
                          domain={domain}
                          selected={selectedIds.includes(
                            decisionMakersResults[virtualItem.index]?.id,
                          )}
                          hideCompanyDetails={true}
                          leadsLists={leadsLists}
                          campaigns={campaigns}
                          onSelect={handleSelect}
                          handleReveal={handleReveal}
                          handleLeadsListSave={handleLeadsListSave}
                          handleCampaignsSave={handleCampaignsSave}
                        />
                      </div>
                    ))}
                  {isLoading && <LoadingSpinner />}
                  {decisionMakersResults.length === 0 &&
                    !isLoading &&
                    (anyFiltersSet ? (
                      <EmptyState clearFilters={clearFilters} />
                    ) : (
                      <EmptyState />
                    ))}
                </div>
              </PillTabsContent>

              <PillTabsContent
                value="generic"
                ref={genericListRef}
                style={{ height: "600px", overflow: "auto" }}
              >
                {!genericRevealed && (
                  <Alert
                    variant="info"
                    size="small"
                    className="mb-2 align-items-center"
                  >
                    <AlertIcon>
                      <span className="h-alert__icon far fa-info-circle"></span>
                    </AlertIcon>
                    <AlertContent className="text-xs">
                      {i18n.t("js.search.generic_email_badge.description", {
                        pretty_count:
                          CurrencyHelper.numberWithDelimiter(
                            genericEmailsCount,
                          ),
                      })}
                    </AlertContent>
                    <Button
                      size="sm"
                      onClick={() => handleReveal(genericResults[0].id)}
                    >
                      {i18n.t("js.search.generic_email_badge.cta")}
                      <ButtonSuffix>
                        <span className="cost-indicator">
                          <Icon
                            icon="coin"
                            size="xs"
                            className="cost-indicator__icon"
                          />
                          1
                        </span>
                      </ButtonSuffix>
                    </Button>
                  </Alert>
                )}
                <div
                  style={
                    genericResults.length > 0
                      ? {
                          height: `${genericListVirtualizer.getTotalSize()}px`,
                          position: "relative",
                        }
                      : undefined
                  }
                >
                  {genericListVirtualizer
                    .getVirtualItems()
                    .map((virtualItem) => (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={genericListVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        <Result
                          result={genericResults[virtualItem.index]}
                          domain={domain}
                          hideCompanyDetails={true}
                          selected={selectedIds.includes(
                            genericResults[virtualItem.index]?.id,
                          )}
                          leadsLists={leadsLists}
                          campaigns={campaigns}
                          onSelect={handleSelect}
                          handleReveal={handleReveal}
                          handleLeadsListSave={handleLeadsListSave}
                          handleCampaignsSave={handleCampaignsSave}
                        />
                      </div>
                    ))}
                  {isLoading && <LoadingSpinner />}
                  {genericResults.length === 0 &&
                    !isLoading &&
                    (anyFiltersSet ? (
                      <EmptyState clearFilters={clearFilters} />
                    ) : (
                      <EmptyState />
                    ))}
                </div>
              </PillTabsContent>
            </PillTabs>
          </div>

          {/* Footer with action buttons */}
          {lastRevealedIds.length > 0 && (
            <Snackbar>
              <Icon
                icon="check-circle"
                variant="solid"
                size="sm"
                className="text-success"
              />
              <SnackbarText>
                <strong className="fw-medium">
                  {i18n.t("js.multi_domain_search.footer.leads", {
                    count: lastRevealedIds.length,
                    pretty_count: CurrencyHelper.numberWithDelimiter(
                      lastRevealedIds.length,
                    ),
                  })}
                </strong>
                &nbsp;
                {i18n.t("js.multi_domain_search.footer.saved")}
              </SnackbarText>
              <SnackbarLink
                href={leads_path(snackbarLinkParams)}
                turboFrame="_top"
              >
                {i18n.t("js.multi_domain_search.footer.see_in_leads")}
              </SnackbarLink>
            </Snackbar>
          )}
        </div>
      </TooltipProvider>
    </>
  )
}
